import React, { CSSProperties, FC, useContext } from "react";
import { ChatConfigContext } from "../../config";
import { isAgent } from "../../utils";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import "./Avatar.scss";

export interface Props {
    readonly entity: ChatUserInfo;
}

const Avatar: FC<React.PropsWithChildren<Props>> = (props) => {
    const style: CSSProperties = {};
    let child: JSX.Element;

    const { entity } = props;

    const chatConfig = useContext(ChatConfigContext);

    let avatarPath = entity?.avatarPath;

    if (!avatarPath) {
        const avatarImage = GenerateAvatar({
            initials: entity?.displayName ? entity.displayName.slice(0, 2) : "?",
            backgroundColor: chatConfig?.env?.theme?.primaryColor,
            size: 36,
        });
        avatarPath = avatarImage.src;
    }

    const agentAva = entity && isAgent(entity.nick);

    if (agentAva) {
        style.backgroundImage = `url('${avatarPath}')`;
        style.backgroundColor = "none";
    } else {
        child = getVisitorSvg();
    }

    const hasImage = !!style.backgroundImage || !!child;

    return (
        <div
            className={`avatar ${agentAva ? "avatar--agent" : "avatar--visitor"} ${!hasImage ? "avatar--empty" : ""
                }`}
            style={style}
            title={entity ? entity.display_name : "Agent"}>
            {child}
        </div>
    );
};

/**
 * Generates an SVG based on the 
 * @returns 
 */
function getVisitorSvg(): JSX.Element {
    return (
        <svg
            width="16"
            height="19"
            viewBox="0 0 16 19"
            style={{ margin: "0 auto", display: "block" }}>
            <path
                d="M11.5 5c0-1.933-1.567-3.5-3.5-3.5S4.5 3.067 4.5 5 6.067 8.5 8 8.5s3.5-1.567 3.5-3.5zM3 5c0-2.76 2.24-5 5-5s5 2.24 5 5-2.24 5-5 5-5-2.24-5-5zM1.955 17.294c.21-.642.504-1.285.898-1.88C3.963 13.74 5.615 12.75 8 12.75c2.385 0 4.038.99 5.147 2.664.394.595.69 1.238.898 1.88.124.382.19.672.214.822.063.41.447.69.856.625.41-.063.69-.447.625-.856-.034-.225-.118-.59-.27-1.053-.247-.763-.598-1.527-1.073-2.244C13.024 12.51 10.917 11.25 8 11.25c-2.916 0-5.024 1.26-6.397 3.336-.475.717-.826 1.48-1.074 2.245-.152.463-.236.83-.27 1.054-.065.41.215.793.624.857.41.065.793-.215.857-.624.025-.15.09-.44.215-.822z"
                fill="#FFF"
                fillRule="evenodd"
            />
        </svg>
    );
}

interface AvatarProps {
    initials?: string;
    size?: number;
    backgroundColor?: string;
    textColor?: string;
}

function GenerateAvatar(props: AvatarProps): HTMLImageElement {
    const initials = props?.initials || "?";
    const size = props?.size || 40;
    const backgroundColor = props?.backgroundColor || "#007bff";
    const textColor = props?.textColor || "#ffffff";

    const image = React.useMemo(() => {
        return generateImage(initials, size, backgroundColor, textColor);
    }, [initials, size, backgroundColor, textColor]);

    return image;
}

function generateImage(initials: string, size: number, backgroundColor: string, textColor: string): HTMLImageElement {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");

    // Draw background
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, size, size);

    // Draw text
    context.fillStyle = textColor;
    context.font = `${size / 2}px Arial`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(initials, size / 2, size / 2);

    // Create image
    const image = new Image();
    image.src = canvas.toDataURL();
    return image;
}

export default Avatar;
